import i18next from 'i18next';

/* eslint-disable @typescript-eslint/no-explicit-any */
export const API_CONFIG = {
  STRAPI_BASE_URL: import.meta.env.VITE_STRAPI_URL,
  GIS_BASE_URL: import.meta.env.VITE_GIS_BASE_URL || 'https://dev-api-gw.opaz.me/gis-proxy/proxyv3',
  GIS_PLOT_STATUS_API_URL:
    import.meta.env.VITE_GIS_PLOT_STATUS_API_URL || 'https://dev-api-gw.opaz.me/gis-api/v3/plots',
  GIS_CMS_URL: import.meta.env.VITE_GIS_CMS_URL || 'https://strapi-dev.opaz.me/api/gis',
  GIS_FILTERS_URL: import.meta.env.VITE_GIS_FILTERS_URL || 'https://dev-backend.opaz.me/api/gis-filter',
  GIS_PORTAL_URL: import.meta.env.VITE_GIS_PORTAL_URL || 'https://dev-portal.opaz.me/start-service',
  GIS_PLOTS_URL: import.meta.env.VITE_GIS_PLOTS_URL || 'https://gisproto.positium.com/proxy_gisapi/OPAZ',
  AUTHORITIES_ENDPOINT: '/authorities',
  SERVICE_CATEGORIES_ENDPOINT: '/service-categories',
  ZONES_ENDPOINT: '/zones',
  SERVICES_ENDPOINT: '/services',
  FAQS_ENDPOINT: '/faqs',
  ABOUT_US_ENDPOINT: '/about-us',
  SEARCH_ENDPOINT: '/search',
};

export const FILTER_OPERATORS = {
  EQUALS: '$eq',
  NOT_EQUALS: '$ne',
  LESS_THAN: '$lt',
  LESS_THAN_OR_EQUAL: '$lte',
  GREATER_THAN: '$gt',
  GREATER_THAN_OR_EQUAL: '$gte',
  IN: '$in',
  NOT_IN: '$notIn',
  CONTAINS: '$contains',
  CONTAINS_CASE_INSENSITIVE: '$containsi',
  IS_NULL: '$null',
  IS_NOT_NULL: '$notNull',
  OR: '$or',
  AND: '$and',
};

// Types for buildStrapiQuery
type StrapiFilter = Record<string, any>;
type StrapiSort = string | string[];
type StrapiPagination = {
  page?: number;
  pageSize?: number;
  withCount?: boolean;
};
type StrapiPopulate = '*' | string[] | Record<string, any>;
type StrapiFields = string[];
type StrapiPublicationState = 'live' | 'preview';

export const buildStrapiQuery = ({
  filters = {},
  sort = [],
  pagination = { page: 1, pageSize: 10 },
  populate = [],
  fields = [],
  publicationState = 'live',
}: {
  filters?: StrapiFilter;
  sort?: StrapiSort;
  pagination?: StrapiPagination;
  populate?: StrapiPopulate;
  fields?: StrapiFields;
  publicationState?: StrapiPublicationState;
}) => {
  const queryParams = new URLSearchParams();

  const locale = i18next.language;

  queryParams.append('locale', locale);

  // Add filters
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== null && typeof value === 'object') {
      Object.entries(value as Record<string, any>).forEach(([operator, opValue]) => {
        if (opValue !== undefined) {
          queryParams.append(`filters[${key}][${operator}]`, String(opValue));
        }
      });
    } else if (value !== undefined) {
      queryParams.append(`filters[${key}]`, String(value));
    }
  });

  // Add sorting
  if (typeof sort === 'string') {
    queryParams.append('sort', sort);
  } else if (Array.isArray(sort)) {
    sort.forEach((item, index) => {
      queryParams.append(`sort[${index}]`, item);
    });
  }

  // Add pagination
  if (pagination?.pageSize === -1) {
    // Disable pagination in Strapi
    queryParams.append('pagination[pageSize]', '-1');
  } else {
    Object.entries(pagination).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(`pagination[${key}]`, String(value));
      }
    });
  }

  // Add populate
  if (populate === '*') {
    queryParams.append('populate', '*');
  } else if (Array.isArray(populate)) {
    populate.forEach((item) => {
      if (typeof item === 'string') {
        queryParams.append(`populate`, item);
      }
    });
  } else if (populate !== null && typeof populate === 'object') {
    Object.entries(populate).forEach(([, popValue]) => {
      queryParams.append(`populate`, JSON.stringify(popValue));
    });
  }

  // Add field selection
  fields.forEach((field, index) => {
    queryParams.append(`fields[${index}]`, field);
  });

  // Add publication state if needed
  if (publicationState === 'preview') {
    queryParams.append('publicationState', publicationState);
  }

  return queryParams.toString();
};
